// Spec 26 — yıllık iznin bölünmesi (LV-10, 4857 m.56 kullanım kuralı).
//
// KURAL (görev tanımından; madde metni repoda yok, "dayanak teyit bekliyor"): bir
// hizmet yılının yıllık izni en çok üç bölüme ayrılabilir ve bölümlerden biri en az
// on gün olmalıdır.
//
// BÖLÜM = kesintisiz izin. İki talep arasında izin günü yoksa (aradaki günler
// hafta tatili ya da resmî tatildir) tek bölümdür: Cuma biten ve Pazartesi başlayan
// iki talep, arka arkaya girilmiş tek izindir. Bu olmasa çalışan on günlük bir
// bloğu iki talebe bölerek kuralı ya aşmış ya da yanlış yere takılmış olurdu. Ucun
// nereye konduğu da cevabı değiştirmez: bölümler talebin GERÇEK başlangıcına ve
// bitişine (ilk ve son izin günü) göre kurulur (leave-duration.ts başlığı, LT-11).
//
// HİZMET YILI. Talep, ilk izin gününün düştüğü hizmet yılına yazılır: LV-11'in
// `serviceYearBreakdown`ı kullanımı aynı kuralla yıllara böler, iki görünüm bir
// talebi iki farklı yıla koyamaz. Yıllar arası bölümler birleşmez.
//
// NE ZAMAN RED, NE ZAMAN UYARI. Çalışanın çıkmaza girmemesi bu ayrımın tek amacıdır.
// İki İHLAL vardır:
//   1. `part_limit_exceeded`: bu talep yılın dördüncü bölümünü açardı.
//   2. `ten_day_part_unreachable`: yılın hakkı on günü buluyor ama talepten sonra en
//      uzun bölüm + kalan bakiye on günün altında kalıyor, yani bu talepten sonra
//      hiçbir kullanım sırası kuralı sağlayamaz.
// İkisi de "bu talep olmasaydı çıkış vardı" durumlarıdır. Kurallar ihlali sıkı
// (`enforce`) ayarda BLOKE eder, `warn` ayarda yalnız gösterir, `off` ayarda hiç
// değerlendirilmez. Üçüncü bir bulgu her zaman yalnız BİLGİdir: yıl iki bölüme
// ayrıldı ama henüz on günlük bölüm yok (`no_ten_day_part_yet`); hâlâ ulaşılabilir.
//
// ÇIKMAZ OLMAMASININ İSPATI (testte kurgu olarak da koşar). Kabul edilen her
// talepten sonra iki değişmez tutar: I1 en çok üç bölüm; I2 yılın hakkı < 10 gün
// ya da (en uzun bölüm + kalan) >= 10 gün. Kalan bakiye varken hep kabul edilen bir
// talep vardır: en uzun bölümün hemen ardındaki izin günlerinden d gün. Bölüm
// sayısı değişmez (I1), en uzun bölüm d uzar ve kalan d azalır, toplam sabittir
// (I2). Hiç bölüm yokken ilk talep de her zaman kabuldür: en uzun + kalan = hak.
// Bu yüzden kural yalnız "araya ayrı bir blok koymayı" engeller, hakkı harcamayı
// hiçbir zaman engellemez. Tek koşul, en uzun bölümün ardındaki günün başka bir izinle
// dolu olmamasıdır; bu takvim koşulu kurala değil çalışana aittir ve İK ayarı `warn`a
// çekerek her durumda çıkış bırakır.
//
// KALAN BAKİYE, hizmet yılının değil hesabın kalanıdır: önceki yıllardan devreden
// gün de sayılır (hak yıldönümünde birikir ve yanmaz, K2). Bu, kuralı GEVŞETİR:
// kalan ne kadar büyükse en uzun bölümü on güne tamamlama imkânı o kadar açıktır ve
// yıl hakkını bakiyeden ayrı izlemek devreden günleri yanlış yıla yazardı. Sahip
// teyidi bekliyor: devir günlerinin hangi yılın bölünmesine sayılacağı.

import { HOURS_PER_WORKING_DAY, firstLeaveDay, lastLeaveDay, nextLeaveDayAfter } from "./leave-duration.ts";
import { completedYearsBetween } from "./annual-leave-entitlement.ts";
import { addIsoDays } from "./timesheet-days.ts";
import type { WorkingWeek } from "./working-week.ts";

/** Bir hizmet yılında en çok kaç bölüm (4857 m.56, dayanak teyit bekliyor). */
export const MAX_ANNUAL_LEAVE_PARTS = 3;
/** Bölümlerden en az birinin uzunluğu, gün (4857 m.56, dayanak teyit bekliyor). */
export const MIN_LONGEST_PART_DAYS = 10;
/**
 * Bitişiklik için en fazla bu kadar gün ileri bakılır: bundan uzun süre izin günü
 * çıkmıyorsa iki talep aynı kesintisiz izin olamaz. Çağıranın tatil okuması bu kadar
 * gün fazlayı kapsamalıdır (`holidayLookaheadEnd`).
 */
export const SPLIT_ADJACENCY_HORIZON_DAYS = 45;

const MIN_LONGEST_PART_HOURS = MIN_LONGEST_PART_DAYS * HOURS_PER_WORKING_DAY;

/** Sıkılık ayarı: tür üzerinde durur, güvenli varsayılan `warn`. */
export const LEAVE_SPLIT_RULES = ["off", "warn", "enforce"] as const;
export type LeaveSplitRule = (typeof LEAVE_SPLIT_RULES)[number];

export interface SplitRequest {
  /** YYYY-MM-DD; ham başlangıç, ilk izin günü bu modülde türetilir. */
  readonly startDate: string;
  readonly endDate: string;
  /** Talebin çektiği saat (sunucu türetimi). */
  readonly hours: number;
}

export type SplitFindingCode = "part_limit_exceeded" | "ten_day_part_unreachable" | "no_ten_day_part_yet";
export type SplitFindingSeverity = "violation" | "advisory";

export interface SplitFinding {
  readonly code: SplitFindingCode;
  readonly severity: SplitFindingSeverity;
}

export interface SplitPart {
  /** İlk izin günü. */
  readonly startDate: string;
  /** Son izin günü. */
  readonly endDate: string;
  readonly days: number;
}

export interface AnnualLeaveSplitVerdict {
  readonly serviceYearIndex: number;
  readonly parts: readonly SplitPart[];
  readonly partCount: number;
  readonly longestPartDays: number;
  readonly remainingDays: number;
  readonly findings: readonly SplitFinding[];
}

export interface AnnualLeaveSplitInput {
  /** İzin hakkı çıpası (hizmet yılı pencerelerini kurar). */
  readonly anchorDate: string;
  /** Tüm taleplerin aralığı + {@link SPLIT_ADJACENCY_HORIZON_DAYS} gün için tatiller. */
  readonly holidayDates: readonly string[];
  readonly workingWeek?: WorkingWeek;
  /** Hesabın DİĞER canlı yıllık izin talepleri (aday hariç, her yıldan). */
  readonly existing: readonly SplitRequest[];
  readonly candidate: SplitRequest;
  /** Aday dahil tüm canlı talepler düşüldükten SONRA hesabın kalan saati. */
  readonly remainingHours: number;
}

const round2 = (value: number): number => Math.round(value * 100) / 100;
const toDays = (hours: number): number => round2(hours / HOURS_PER_WORKING_DAY);

/** Çağıranın tatil okumasının bitişi: en geç talep bitişi + ileri bakış. */
export function holidayLookaheadEnd(latestEndDate: string): string {
  return addIsoDays(latestEndDate, SPLIT_ADJACENCY_HORIZON_DAYS);
}

interface Placed {
  readonly first: string;
  readonly last: string;
  readonly hours: number;
}

/**
 * Adayın yılındaki bölümler ve bulgular. İzin günü içermeyen aday (yalnız hafta
 * sonu/tatil aralığı) için null: bölünecek bir şey yok, gönderim yolu onu zaten
 * ayrıca reddeder.
 */
export function evaluateAnnualLeaveSplit(input: AnnualLeaveSplitInput): AnnualLeaveSplitVerdict | null {
  const holidays = new Set(input.holidayDates);
  const place = (request: SplitRequest): Placed | null => {
    const window = {
      startDate: request.startDate,
      endDate: request.endDate,
      holidayDates: input.holidayDates,
      ...(input.workingWeek !== undefined && { workingWeek: input.workingWeek }),
    };
    const first = firstLeaveDay(window);
    const last = lastLeaveDay(window);
    return first === null || last === null ? null : { first, last, hours: request.hours };
  };

  const candidate = place(input.candidate);
  if (candidate === null) return null;
  const yearIndex = completedYearsBetween(input.anchorDate, candidate.first);
  const inYear = (day: string): boolean => completedYearsBetween(input.anchorDate, day) === yearIndex;

  const members = [
    candidate,
    ...input.existing.map(place).filter((p): p is Placed => p !== null && inYear(p.first)),
  ].sort((a, b) => a.first.localeCompare(b.first) || a.last.localeCompare(b.last));

  const merged: { first: string; last: string; hours: number }[] = [];
  for (const member of members) {
    const current = merged[merged.length - 1];
    const next =
      current === undefined
        ? null
        : nextLeaveDayAfter(current.last, holidays, input.workingWeek, SPLIT_ADJACENCY_HORIZON_DAYS);
    // `<=`: ardışık günden ÖNCE başlayan (örtüşen) talep de aynı bölümdür.
    if (current !== undefined && (member.first <= current.last || (next !== null && member.first <= next))) {
      if (member.last > current.last) current.last = member.last;
      current.hours += member.hours;
    } else {
      merged.push({ first: member.first, last: member.last, hours: member.hours });
    }
  }

  const parts: SplitPart[] = merged.map((p) => ({ startDate: p.first, endDate: p.last, days: toDays(p.hours) }));
  const longestHours = Math.max(...merged.map((p) => p.hours));
  const remainingHours = Math.max(0, input.remainingHours);
  const totalHours = merged.reduce((sum, p) => sum + p.hours, 0) + remainingHours;

  const findings: SplitFinding[] = [];
  if (merged.length > MAX_ANNUAL_LEAVE_PARTS) {
    findings.push({ code: "part_limit_exceeded", severity: "violation" });
  }
  const applies = round2(totalHours) >= MIN_LONGEST_PART_HOURS;
  const reachable = round2(longestHours + remainingHours) >= MIN_LONGEST_PART_HOURS;
  if (applies && !reachable) {
    findings.push({ code: "ten_day_part_unreachable", severity: "violation" });
  } else if (applies && merged.length >= 2 && round2(longestHours) < MIN_LONGEST_PART_HOURS) {
    findings.push({ code: "no_ten_day_part_yet", severity: "advisory" });
  }

  return {
    serviceYearIndex: yearIndex,
    parts,
    partCount: parts.length,
    longestPartDays: toDays(longestHours),
    remainingDays: toDays(remainingHours),
    findings,
  };
}

/** Bulgulardan biri kuralın seçili sıkılığında bloke eder mi. */
export function splitVerdictBlocks(rule: LeaveSplitRule, verdict: AnnualLeaveSplitVerdict | null): boolean {
  return (
    rule === "enforce" && verdict !== null && verdict.findings.some((finding) => finding.severity === "violation")
  );
}
